import { Component, computed, ViewChild } from '@angular/core';
import {
  Agency,
  AGENCY_TYPE,
  AgencyKeys,
  AgentKeys,
  Entity,
  EntityPermissionActivityKeys,
  EntityPermissionModelKeys,
  LookupKeys,
  Lookups,
  MediaSize,
  Messages,
} from '@ag-common-lib/public-api';
import { ModalWindowComponent } from '../../../modal-window/modal-window.component';
import { DxFormComponent } from 'devextreme-angular';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AGENCY_STATUS, AGENCY_TYPES_LOOKUP } from '@ag-common-lib/lib';
import { ChangeSourceTypeService, LookupsService } from 'ag-common-svc/public-api';
import { FullAddressPipe } from 'ag-common-svc/shared/pipes/full-address.pipe';
import { PhoneNumberMaskPipe } from 'ag-common-svc/shared/pipes/phone-number-mask.pipe';
import { WasabiImgPipe } from 'ag-common-svc/shared/pipes/wasabi-img.pipe';
import { AgencyInfoModalService } from './agency-info-modal.service';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { AgencyInfoModalKeys } from './agency-info-modal.model';
import { ResizeEvent } from 'devextreme/ui/popup';
import { UpdateAgencyLeadModalComponent } from '../update-agency-lead-modal/update-agency-lead-modal.component';
import { AgentsTransferModalService } from 'ag-common-svc/lib/modules/agency-hierarchy-administration/agency-hierarchy-management/agents-transfer-modal.service';
import { combineLatest, map, Observable } from 'rxjs';
import { AgencyHierarchyManagerMode } from 'ag-common-svc/lib/modules/agency-hierarchy-administration/agency-hierarchy-management/agency-hierarchy-management.model';
import { ToastrService } from 'ngx-toastr';

interface DxFormValidateResult {
  isValid?: boolean;
  complete?: Promise<{ isValid?: boolean }>;
}

@UntilDestroy()
@Component({
  selector: 'ag-shr-agency-info-modal',
  templateUrl: './agency-info-modal.component.html',
  styleUrls: ['./agency-info-modal.component.scss'],
  providers: [AgencyInfoModalService, PhoneNumberMaskPipe, FullAddressPipe, WasabiImgPipe, AgentsTransferModalService],
})
export class AgencyInfoModalComponent {
  @ViewChild('agencyInfoModalRef', { static: true }) agencyInfoModalModalComponent: ModalWindowComponent;
  @ViewChild('updaterAgencyLeadModalRef', { static: true })
  updaterAgencyLeadModalComponent: UpdateAgencyLeadModalComponent;
  @ViewChild('agencyInfoModalFormRef', { static: false }) agencyInfoModalFormComponent: DxFormComponent;

  protected inProgress$ = this.agencyInfoModalService.inProgress$;
  protected agencyLeadsSig = computed(() => {
    const agency = this.agencyInfoModalService.formDataSig();
    const allAgencyLeads = this.agencyInfoModalService.allAgencyLeadsSig();

    return allAgencyLeads.filter(principal => {
      const agencyId = principal?.[AgentKeys.p_agency_id];
      const agentId = principal?.[AgentKeys.p_agent_id];
      return agentId && agencyId && agencyId === agency?.[AgencyKeys.agencyId];
    });
  });
  t: ResizeEvent;
  protected mgasLookup$ = this.agencyInfoModalService.mgasLookup$;
  protected rmds$ = this.agencyInfoModalService.rmds$;
  protected agencyStatusesLookup$ = this.lookupsService.agencyStatusesLookup$;
  protected EntityPermissionActivityKeys = EntityPermissionActivityKeys;
  protected agencyFormDataSig = this.agencyInfoModalService.formDataSig;

  protected readonly AgencyKeys = AgencyKeys;
  protected readonly AgentKeys = AgentKeys;
  protected readonly LookupKeys = LookupKeys;
  protected readonly Lookups = Lookups;
  protected readonly agencyStatus = AGENCY_STATUS;
  protected readonly BaseModelKeys = BaseModelKeys;
  protected readonly agencyType = AGENCY_TYPE;
  protected readonly AGENCY_TYPES_LOOKUP = AGENCY_TYPES_LOOKUP;
  protected readonly AgencyInfoModalKeys = AgencyInfoModalKeys;
  protected readonly MediaSize = MediaSize;
  protected readonly EntityPermissionModelKeys = EntityPermissionModelKeys;
  protected readonly Entity = Entity;

  protected readonly AgencyHierarchyManagerMode = AgencyHierarchyManagerMode;

  protected inTransferInProgress$: Observable<boolean> = combineLatest([
    this.agencyInfoModalService.isTransferInProgress$,
    this.agentsTransferModalService.inProgress$,
  ]).pipe(map(items => items?.some(Boolean)));
  protected promptSettingsSig = this.agentsTransferModalService.promptSettingsSig;
  protected agentTransferConfigSig = this.agentsTransferModalService.agentTransferConfigSig;
  protected readonly transferPromptModalId = this.agentsTransferModalService.transferPromptModalId;
  protected readonly transferModalId = this.agentsTransferModalService.transferModalId;
  protected readonly Messages = Messages;

  constructor(
    private agencyInfoModalService: AgencyInfoModalService,
    private phoneNumberMaskPipe: PhoneNumberMaskPipe,
    private fullAddressPipe: FullAddressPipe,
    private lookupsService: LookupsService,
    private agentsTransferModalService: AgentsTransferModalService,
    private toastrService: ToastrService,
  ) {}

  protected handleSave = async () => {
    const formInstance = this.agencyInfoModalFormComponent?.instance;
    if (!formInstance) {
      return;
    }

    this.clearAgencyDuplicateEditorErrors(formInstance);

    const { isValid: formRulesValid } = await this.getFormValidationOutcome(formInstance);

    const agency = this.agencyFormDataSig();
    const dupName = this.agencyInfoModalService.isDuplicateAgencyName(agency?.[AgencyKeys.name]);
    const dupId = this.agencyInfoModalService.isDuplicateAgencyId(agency?.[AgencyKeys.agencyId]);
    if (dupName) {
      this.setAgencyDuplicateEditorError(formInstance, AgencyKeys.name, Messages.DUPLICATE_AGENCY_NAME_MESSAGE);
    }
    if (dupId) {
      this.setAgencyDuplicateEditorError(formInstance, AgencyKeys.agencyId, Messages.DUPLICATE_AGENCY_ID_MESSAGE);
    }

    if (!formRulesValid || dupName || dupId) {
      return;
    }

    try {
      await this.agencyInfoModalService.handleSave();
      this.agencyInfoModalModalComponent.forceCloseModal();
    } catch (error) {
      this.toastrService.error('Agency was not saved.');
      throw error;
    }
  };

  // Duplicates cannot slip through regardless of DevExtreme timing and validation messages show only when Save is used
  private async getFormValidationOutcome(formInstance: { validate: () => unknown }): Promise<{ isValid: boolean }> {
    const raw = formInstance.validate();

    if (typeof raw === 'boolean') {
      return { isValid: raw };
    }

    if (raw && typeof (raw as PromiseLike<{ isValid?: boolean }>).then === 'function') {
      const resolved = await (raw as Promise<{ isValid?: boolean }>);
      return { isValid: resolved?.isValid !== false };
    }

    const sync = raw as DxFormValidateResult;
    let isValid = sync?.isValid !== false;
    if (sync?.complete && typeof sync.complete.then === 'function') {
      const asyncResolved = await sync.complete;
      isValid = isValid && asyncResolved?.isValid !== false;
    }
    return { isValid };
  }

  private clearAgencyDuplicateEditorErrors(formInstance: { getEditor: (name: string) => { option: (k: string, v?: unknown) => void } | undefined }): void {
    this.clearEditorValidationError(formInstance, AgencyKeys.name);
    this.clearEditorValidationError(formInstance, AgencyKeys.agencyId);
  }

  private clearEditorValidationError(
    formInstance: { getEditor: (name: string) => { option: (k: string, v?: unknown) => void } | undefined },
    dataField: string,
  ): void {
    const editor = formInstance.getEditor(dataField);
    if (!editor) {
      return;
    }
    editor.option('validationError', null);
    editor.option('isValid', true);
  }

  private setAgencyDuplicateEditorError(
    formInstance: { getEditor: (name: string) => { option: (k: string, v?: unknown) => void } | undefined },
    dataField: string,
    message: string,
  ): void {
    const editor = formInstance.getEditor(dataField);
    if (!editor) {
      return;
    }
    editor.option('validationError', { message });
    editor.option('isValid', false);
  }

  showModal = (agency?: Agency): void => {
    this.agencyInfoModalService.getFormData(agency);
    this.agencyInfoModalModalComponent.showModal();
  };

  protected handleClosePopup = this.agencyInfoModalService.onCancelEdit;

  protected phoneNumberDisplayExpr = item => this.phoneNumberMaskPipe.transform(item, true);

  protected fullAddressDisplayExpr = item => this.fullAddressPipe.transform(item);

  protected onSelectionChanged = ({ selectedItem }): void => {
    this.agencyInfoModalService.setSelectedAgencyStatus(selectedItem);
  };

  protected percentageDisplayExpression = value => `${value * 100}%`;

  protected handleUpdateAgencyLead = () => {
    this.updaterAgencyLeadModalComponent.showModal();
  };
}
